/**
 * Single source of truth for public, indexable marketing/SEO routes.
 *
 * Consumed by:
 *  - app/routes/sitemap[.]xml.tsx  (sitemap generation)
 *  - app/components/seo/SEOLayout.tsx (footer internal-linking)
 *  - app/root.tsx (public-route allowlist)
 *
 * Keeping these in one place prevents drift between the sitemap, the crawl
 * path (footer links), and which routes are reachable without auth.
 */

export interface SeoRoute {
  /** Absolute path, leading slash, no trailing slash (except "" for home). */
  path: string;
  /** Short human label for footer/nav link text. */
  label: string;
  /** One-line description used as the contextual link/card copy on hub pages. */
  description?: string;
}

/**
 * A long-form content block rendered on a hub page (heading + prose and/or a
 * labelled bullet list). Used to give hub index pages real editorial depth
 * beyond the intro + card grid, so they don't read as thin content.
 */
export interface SeoHubSection {
  /** Section <h2>. */
  heading: string;
  /** Body paragraphs. */
  paragraphs?: string[];
  /** Labelled bullets — the label is emphasised, the text follows. */
  bullets?: { label: string; text: string }[];
}

/**
 * Hub (category index) page metadata. Each hub at {hubPath} lists its child
 * routes with descriptive, contextual links — turning a flat homepage→leaf
 * structure into hub-and-spoke so crawlers get a strong, topical crawl path.
 */
export interface SeoHub {
  /** Category index path, e.g. "/solutions". */
  hubPath: string;
  /** <h1> on the hub page. */
  hubH1: string;
  /** <title> for the hub page. */
  hubTitle: string;
  /** Meta description for the hub page. */
  hubDescription: string;
  /** Intro paragraph rendered under the H1. */
  hubIntro: string;
  /** Optional long-form content sections rendered below the card grid. */
  hubSections?: SeoHubSection[];
}

export interface SeoRouteGroup extends SeoHub {
  /** Footer column heading / hub short name. */
  title: string;
  routes: SeoRoute[];
}

/** Game landing pages — /seo/{game} */
export const GAME_ROUTES: SeoRoute[] = [
  { path: "/seo/spin-the-wheel", label: "Spin the Wheel", description: "The classic prize wheel — customers spin to win discounts and offers on your store." },
  { path: "/seo/scratch-reveal", label: "Scratch Card", description: "Digital scratch cards that reveal a hidden reward with a satisfying scratch." },
  { path: "/seo/pick-a-box", label: "Pick a Box", description: "Players choose one of several mystery boxes to uncover their prize." },
  { path: "/seo/plinko", label: "Plinko", description: "A drop-the-chip Plinko board where the landing slot decides the reward." },
  { path: "/seo/claw-machine", label: "Claw Machine", description: "An arcade claw grab for a randomized, playful reward." },
  { path: "/seo/basketball", label: "Basketball", description: "Shoot hoops on the thank-you page to unlock a discount." },
  { path: "/seo/hit-the-target", label: "Hit the Target", description: "An aim-and-shoot target game that hands out instant prizes." },
  { path: "/seo/lucky-dice", label: "Lucky Dice", description: "Roll the dice to reveal a reward and keep customers engaged." },
  { path: "/seo/break-ice-cube", label: "Break the Ice", description: "Tap to break the ice cube and reveal what's frozen inside." },
];

/** Solution / use-case pages — /solutions/* */
export const SOLUTION_ROUTES: SeoRoute[] = [
  { path: "/solutions/reduce-cart-abandonment", label: "Reduce Cart Abandonment", description: "Win back hesitant shoppers with a game before they leave checkout." },
  { path: "/solutions/increase-aov", label: "Increase AOV", description: "Nudge larger baskets with reward thresholds and spend-to-unlock prizes." },
  { path: "/solutions/post-purchase-engagement", label: "Post-Purchase Engagement", description: "Turn the thank-you page into a second, revenue-driving touchpoint." },
  { path: "/solutions/customer-retention", label: "Customer Retention", description: "Bring one-time buyers back for repeat orders with rewards." },
  { path: "/solutions/gamified-checkout", label: "Gamified Checkout", description: "Add a game to checkout to lift conversion and order completion." },
  { path: "/solutions/email-list-growth", label: "Email List Growth", description: "Capture more emails with an opt-in, play-to-win popup." },
];

/** Competitor comparison pages — /compare/* */
export const COMPARE_ROUTES: SeoRoute[] = [
  { path: "/compare/wheelio", label: "Prizo vs Wheelio", description: "How Prizo stacks up against Wheelio for Shopify gamification." },
  { path: "/compare/privy", label: "Prizo vs Privy", description: "Prizo vs Privy — gamified games and discounts compared." },
  { path: "/compare/spin-wheel-apps", label: "Best Spin Wheel Apps", description: "The best spin-the-wheel apps for Shopify, compared side by side." },
];

/** Industry pages — /for/{industry} */
export const INDUSTRY_ROUTES: SeoRoute[] = [
  { path: "/for/fashion", label: "Fashion", description: "Gamification built for fashion & apparel Shopify stores." },
  { path: "/for/beauty", label: "Beauty", description: "Gamification for beauty & cosmetics brands." },
  { path: "/for/food-beverage", label: "Food & Beverage", description: "Gamification for food & beverage shops." },
  { path: "/for/electronics", label: "Electronics", description: "Gamification for electronics & gadget stores." },
];

/** Blog / guide articles — /blog/{slug} */
export const BLOG_ROUTES: SeoRoute[] = [
  { path: "/blog/how-to-reduce-cart-abandonment", label: "How to Reduce Cart Abandonment", description: "10 proven tactics to lower your shopping cart abandonment rate and recover more carts." },
  { path: "/blog/how-to-increase-average-order-value", label: "How to Increase Average Order Value", description: "The AOV formula plus 8 proven ways to lift average order value on Shopify." },
  { path: "/blog/customer-retention-strategies", label: "Customer Retention Strategies", description: "Turn one-time buyers into repeat customers and grow lifetime value." },
  { path: "/blog/ecommerce-gamification", label: "Ecommerce Gamification Guide", description: "What ecommerce gamification is, real examples, and how to use it to grow." },
  { path: "/blog/spin-the-wheel-popup-best-practices", label: "Spin the Wheel Popup Best Practices", description: "Set up a spin-the-wheel popup on Shopify the right way — rewards, odds, and placement." },
];

/** Standalone marketing pages. */
export const CORE_ROUTES: SeoRoute[] = [
  { path: "/landing", label: "Home" },
  { path: "/faq", label: "FAQ" },
  { path: "/privacy", label: "Privacy" },
  { path: "/terms", label: "Terms" },
];

/** Grouped view for rendering the footer link map and the hub (index) pages. */
export const SEO_ROUTE_GROUPS: SeoRouteGroup[] = [
  {
    title: "Games",
    hubPath: "/seo",
    hubH1: "Shopify Gamification Games",
    hubTitle: "Shopify Gamification Games — Spin the Wheel, Scratch & More | Prizo",
    hubDescription:
      "Every Prizo game you can add to your Shopify store — spin the wheel, scratch cards, Plinko, claw machine and more — to reward customers and drive repeat sales.",
    hubIntro:
      "Every Prizo game you can add to your Shopify store, from the classic spin-the-wheel to scratch cards, Plinko, and more. Each game runs on the post-purchase thank-you page or as a popup to capture emails, reward customers, and drive repeat sales.",
    routes: GAME_ROUTES,
  },
  {
    title: "Solutions",
    hubPath: "/solutions",
    hubH1: "Gamification Solutions for Shopify",
    hubTitle: "Shopify Gamification Solutions — Cart Recovery, AOV & Retention | Prizo",
    hubDescription:
      "See how Shopify merchants use Prizo to recover abandoned carts, increase average order value, grow email lists, and turn one-time buyers into repeat customers.",
    hubIntro:
      "See how Shopify merchants use Prizo to solve real revenue problems — recovering abandoned carts, increasing average order value, growing email lists, and turning one-time buyers into repeat customers.",
    routes: SOLUTION_ROUTES,
  },
  {
    title: "Compare",
    hubPath: "/compare",
    hubH1: "Compare Prizo to Other Shopify Apps",
    hubTitle: "Prizo vs Other Shopify Gamification & Popup Apps | Prizo",
    hubDescription:
      "How Prizo compares to other Shopify gamification and popup apps — spin-the-wheel games, discounts, and email capture, side by side.",
    hubIntro:
      "How Prizo compares to other Shopify gamification and popup apps, so you can choose the right tool for spin-the-wheel games, discounts, and email capture.",
    hubSections: [
      {
        heading: "What to look for in a Shopify gamification app",
        paragraphs: [
          "Most gamification and popup apps for Shopify do one thing: show a spin-the-wheel popup that trades a discount for an email address. That works — but it only touches shoppers before they buy, and it treats every store the same. Before you commit to a tool, it helps to compare apps on the things that actually move revenue, not just the ones with the flashiest popup.",
        ],
        bullets: [
          { label: "Game variety", text: "A single spinning wheel gets ignored once customers have seen it twice. Prizo ships nine games — spin the wheel, scratch cards, Plinko, claw machine, basketball and more — so the experience stays fresh and worth playing again." },
          { label: "Where the game runs", text: "Popup-only apps fire before checkout and stop there. Prizo also runs on the post-purchase thank-you page, turning a dead order-confirmation screen into a second, revenue-driving touchpoint that lifts repeat orders." },
          { label: "Email capture quality", text: "Every serious app captures emails; the difference is the trade. Prizo gates rewards behind an opt-in, so the list you build is made of real buyers and engaged shoppers rather than throwaway addresses." },
          { label: "Discount automation", text: "Prizo generates a unique, single-use Shopify discount code for each player and applies it automatically at checkout, so you never hand out a reusable coupon that leaks across the internet." },
          { label: "Branding & localization", text: "Custom colors, copy, logo and full Hebrew/RTL support mean the game looks like your store, in your customer's language — not a generic third-party widget bolted on." },
          { label: "Pricing & risk", text: "Prizo offers a free trial and transparent plans, so you can prove the lift on your own store before you pay for it." },
        ],
      },
      {
        heading: "What makes Prizo different",
        paragraphs: [
          "Where most apps are a pre-purchase spin wheel, Prizo is a full gamification platform that spans the whole customer journey. A single install can capture an email with an opt-in popup, reward a completed order on the thank-you page, and bring that customer back later — all with games you can theme to your brand.",
          "That breadth is why merchants moving off single-game tools tend to consolidate on Prizo: one app covers cart recovery, average-order-value nudges, email-list growth and retention, instead of stitching several point solutions together and paying for each.",
        ],
      },
      {
        heading: "Compare Prizo head-to-head",
        paragraphs: [
          "The comparisons below break Prizo down against specific apps and categories — feature by feature — so you can see exactly where each one fits your store before you install anything.",
        ],
      },
    ],
    routes: COMPARE_ROUTES,
  },
  {
    title: "For Your Industry",
    hubPath: "/for",
    hubH1: "Shopify Gamification by Industry",
    hubTitle: "Shopify Gamification by Industry — Fashion, Beauty & More | Prizo",
    hubDescription:
      "Prizo gamification tailored to your vertical — fashion, beauty, food & beverage, and electronics stores using post-purchase games to boost conversions.",
    hubIntro:
      "Prizo gamification tailored to your vertical — see how fashion, beauty, food & beverage, and electronics stores use post-purchase games to boost conversions and retention.",
    hubSections: [
      {
        heading: "Why gamification works differently by industry",
        paragraphs: [
          "A spin-to-win game that delights a fashion shopper can fall flat in an electronics store. The reward that drives a repeat coffee order isn't the one that sells a second lipstick. Gamification works best when the game, the prize and the timing match how people actually buy in your category — which is why Prizo tailors the experience by vertical instead of dropping the same wheel on every store.",
        ],
      },
      {
        heading: "How each vertical uses Prizo",
        bullets: [
          { label: "Fashion & apparel", text: "High purchase frequency and impulse buying make fashion ideal for post-purchase games. Reward a completed order with a discount on the next drop to turn one-time buyers into repeat customers and keep them coming back each season." },
          { label: "Beauty & cosmetics", text: "Replenishment is everything in beauty. Use games to capture emails for restock reminders and hand out rewards that bring shoppers back before they run out of a product they already love." },
          { label: "Food & beverage", text: "Low margins and habitual buying reward loyalty. Gamified thank-you offers and points keep customers ordering on a regular cadence and lift repeat-purchase rate without deep discounting." },
          { label: "Electronics & gadgets", text: "Higher prices and longer consideration mean fewer impulse wins. Use games to grow an email list and offer accessory or trade-up discounts that increase average order value on bigger baskets." },
        ],
      },
      {
        heading: "The same platform, tuned to your store",
        paragraphs: [
          "Whatever you sell, Prizo runs the same way: choose from nine games, brand them with your colors, logo and language (including full Hebrew/RTL), and place them as a pre-purchase popup or on the post-purchase thank-you page. Rewards are issued as unique, single-use Shopify discount codes that apply automatically at checkout.",
          "Explore your vertical below to see the games, rewards and placements that work best for stores like yours.",
        ],
      },
    ],
    routes: INDUSTRY_ROUTES,
  },
  {
    title: "Blog",
    hubPath: "/blog",
    hubH1: "Shopify Gamification & Ecommerce Growth Blog",
    hubTitle: "Prizo Blog — Shopify Gamification, Conversion & Retention Guides",
    hubDescription:
      "Guides on ecommerce gamification, reducing cart abandonment, increasing average order value, customer retention, and growing your Shopify store.",
    hubIntro:
      "Practical guides for Shopify merchants — how to reduce cart abandonment, increase average order value, retain more customers, and use gamification to grow. Written by the team behind Prizo.",
    hubSections: [
      {
        heading: "Grow your Shopify store with gamification",
        paragraphs: [
          "Prizo's blog focuses on the levers that actually move ecommerce revenue: converting more of the traffic you already have, getting customers to spend more per order, and bringing them back to buy again. Each guide is built to be genuinely useful first — clear definitions, real tactics, and honest trade-offs — with gamification covered as one tool among many.",
          "Start with the topic closest to your current goal, whether that's recovering abandoned carts, lifting average order value, improving customer retention, or understanding ecommerce gamification from the ground up.",
        ],
      },
    ],
    routes: BLOG_ROUTES,
  },
];

/** Just the hub (category index) routes — used for cross-hub linking + sitemap. */
export const HUB_ROUTES: SeoRoute[] = SEO_ROUTE_GROUPS.map((g) => ({
  path: g.hubPath,
  label: g.title,
  description: g.hubDescription,
}));

/** Look up a hub group by its index path (e.g. "/solutions"). */
export function getHubGroup(hubPath: string): SeoRouteGroup | undefined {
  return SEO_ROUTE_GROUPS.find((g) => g.hubPath === hubPath);
}

/**
 * Every programmatic SEO route (no core/legal pages). Useful for sitemap
 * priority handling and the public-route allowlist.
 */
export const ALL_SEO_ROUTES: SeoRoute[] = [
  ...GAME_ROUTES,
  ...SOLUTION_ROUTES,
  ...COMPARE_ROUTES,
  ...INDUSTRY_ROUTES,
  ...BLOG_ROUTES,
];

/**
 * Flat list of all public paths for the sitemap.
 *
 * NOTE: the home entry is the bare root "" (-> https://prizo.io), NOT "/landing".
 * Both "/" and "/landing" render the marketing page, but they declare canonical
 * "/", so "/landing" canonicalizes away. Listing "/landing" in the sitemap was
 * flagged as an "incorrect page in sitemap" (a non-canonical URL); listing the
 * canonical "" / "/" instead resolves it.
 */
export const SITEMAP_PATHS: string[] = [
  "",
  "/faq",
  "/privacy",
  "/terms",
  // Hub (category index) pages first — they sit one level above the leaves.
  ...HUB_ROUTES.map((r) => r.path),
  ...ALL_SEO_ROUTES.map((r) => r.path),
];

/** Look up the short human label for a public SEO path (used for breadcrumbs). */
export function seoRouteLabel(path: string): string | undefined {
  return ALL_SEO_ROUTES.find((r) => r.path === path)?.label;
}

/** Set of public paths that must render without authentication. */
export const PUBLIC_SEO_PATHS: string[] = [
  "/landing",
  "/faq",
  "/privacy",
  "/terms",
  ...HUB_ROUTES.map((r) => r.path),
  ...ALL_SEO_ROUTES.map((r) => r.path),
];
